import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Music, Map as MapIcon, List } from 'lucide-react';
import MusicianCard from '../components/search/MusicianCard';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSearchFilters } from '../contexts/SearchFiltersContext';
import AlertMessage from '../components/common/AlertMessage';
import LocationSelector from '../components/forms/LocationSelector';
import { INSTRUMENT_OPTIONS } from '../shared/avatars';

interface MusicianProfile {
  id: string;
  name: string;
  instrument: string;
  skillLevel: string;
  birthYear?: number;
  genres?: string[];
  status?: string;
  address?: string;
  location?: {
    lat: number;
    lng: number;
  };
}

interface MusicianCardData extends MusicianProfile {
  distance: number | null;
  age: number | null;
}

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];

const instruments = ['All Instruments', ...INSTRUMENT_OPTIONS];

const skillLevels = ['All Levels', 'Beginner', 'Intermediate', 'Advanced', 'Professional'];

const normalizeGenreKey = (genre: string): string => genre.trim().toLowerCase();

const formatGenreLabel = (genre: string): string =>
  genre
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const SearchPage: React.FC = () => {
  const { user } = useAuth();
  const {
    selectedInstrument,
    setSelectedInstrument,
    selectedSkillLevel,
    setSelectedSkillLevel,
    maxDistance,
    setMaxDistance,
    selectedGenres,
    setSelectedGenres,
    searchLocation,
    setSearchLocation,
    searchAddress,
    setSearchAddress,
  } = useSearchFilters();
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [debouncedMaxDistance, setDebouncedMaxDistance] = useState(maxDistance);
  const [selectedMusician, setSelectedMusician] = useState<string | null>(null);
  const [musicians, setMusicians] = useState<MusicianProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const pageSize = 24;

  // Get Google Maps API key from environment variables
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const defaultCenter = {
    lat: 45.4642, // Milan, Italy
    lng: 9.1900
  };

  const handleLocationSelect = (location: { lat: number; lng: number }, address: string) => {
    setSearchLocation(location);
    setSearchAddress(address);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedMaxDistance(maxDistance);
    }, 700);

    return () => window.clearTimeout(timer);
  }, [maxDistance]);

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback(
    (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      const R = 6371; // Earth's radius in kilometers
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) *
          Math.cos(lat2 * Math.PI / 180) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    []
  );

  const getNumericValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const getAgeFromBirthYear = (birthYear?: number | null): number | null => {
    if (typeof birthYear !== 'number' || !Number.isFinite(birthYear)) return null;
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear;
    if (age < 0 || age > 120) return null;
    return age;
  };

  const normalizeGenres = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];

    const distinctGenres = new Map<string, string>();

    value
      .filter((genre) => typeof genre === 'string')
      .map((genre) => genre.trim())
      .filter((genre) => genre.length > 0)
      .forEach((genre) => {
        const key = normalizeGenreKey(genre);
        if (!distinctGenres.has(key)) {
          distinctGenres.set(key, formatGenreLabel(genre));
        }
      });

    return Array.from(distinctGenres.values());
  };

  const normalizeMusician = (value: any): MusicianProfile => {
    const lat = getNumericValue(value?.location?.lat);
    const lng = getNumericValue(value?.location?.lng);
    const birthYear = getNumericValue(value?.birthYear);

    return {
      id: String(value?.id ?? ''),
      name: String(value?.name ?? value?.username ?? value?.email ?? 'Unknown musician'),
      instrument: String(value?.instrument ?? ''),
      skillLevel: String(value?.skillLevel ?? ''),
      birthYear: birthYear ?? undefined,
      genres: normalizeGenres(value?.genres),
      status: typeof value?.status === 'string' ? value.status : undefined,
      address: typeof value?.address === 'string' ? value.address : undefined,
      location: lat !== null && lng !== null ? { lat, lng } : undefined,
    };
  };

  const referenceLocation =
    searchLocation ||
    (user?.location &&
    Number.isFinite(user.location.lat) &&
    Number.isFinite(user.location.lng)
      ? user.location
      : null);

  const buildSearchFilters = useCallback(
    (nextOffset: number) => {
      const filters: Record<string, string | number | Array<string | number>> = {
        sort: 'name',
        limit: pageSize,
        offset: nextOffset,
        status: 'ACTIVE',
      };

      if (selectedInstrument !== 'All Instruments') {
        filters.instrument = selectedInstrument;
      }

      if (selectedSkillLevel !== 'All Levels') {
        filters.skillLevel = selectedSkillLevel;
      }

      if (searchLocation) {
        filters['location.lat'] = searchLocation.lat;
        filters['location.lng'] = searchLocation.lng;
        filters.distance = Math.round(debouncedMaxDistance * 1000);
      }

      return filters;
    },
    [debouncedMaxDistance, pageSize, searchLocation, selectedInstrument, selectedSkillLevel]
  );

  const loadMusicians = useCallback(
    async (nextOffset: number, shouldAppend: boolean) => {
      setIsLoading(true);
      setSearchError(null);

      try {
        const response = await apiService.searchMusicians(buildSearchFilters(nextOffset));
        const results = Array.isArray(response) ? response.map(normalizeMusician) : [];

        setMusicians((prev) => (shouldAppend ? [...prev, ...results] : results));
        setHasMoreResults(results.length === pageSize);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchError(error instanceof Error ? error.message : 'Search failed. Please try again.');
        if (!shouldAppend) {
          setMusicians([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [buildSearchFilters, pageSize]
  );

  useEffect(() => {
    setSelectedGenres((prev) => (prev.length > 0 ? [] : prev));
    setOffset(0);
    setHasMoreResults(true);
    loadMusicians(0, false);
  }, [loadMusicians, setSelectedGenres]);

  const loadMore = useCallback(() => {
    if (isLoading || !hasMoreResults) return;
    const nextOffset = offset + pageSize;
    setOffset(nextOffset);
    loadMusicians(nextOffset, true);
  }, [hasMoreResults, isLoading, loadMusicians, offset, pageSize]);

  useEffect(() => {
    const handleScroll = () => {
      if (isLoading || !hasMoreResults) return;
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.body.offsetHeight - 200;

      if (scrollPosition >= threshold) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMoreResults, isLoading, loadMore]);

  const baseFilteredMusicians = useMemo(() => musicians
    .map((musician): MusicianCardData => {
      const age = getAgeFromBirthYear(musician.birthYear);
      if (referenceLocation && musician.location) {
        const distance = calculateDistance(
          referenceLocation.lat,
          referenceLocation.lng,
          musician.location.lat,
          musician.location.lng
        );
        return { ...musician, distance: Math.round(distance), age };
      }
      return { ...musician, distance: null, age };
    })
    .filter((musician) => {
      const isNotCurrentUser = !user?.id || musician.id !== user.id;
      const matchesInstrument =
        selectedInstrument === 'All Instruments' || musician.instrument === selectedInstrument;
      const matchesSkillLevel =
        selectedSkillLevel === 'All Levels' || musician.skillLevel === selectedSkillLevel;
      const matchesDistance = musician.distance === null || musician.distance <= debouncedMaxDistance;
      return isNotCurrentUser && matchesInstrument && matchesSkillLevel && matchesDistance;
    }), [calculateDistance, debouncedMaxDistance, musicians, referenceLocation, selectedInstrument, selectedSkillLevel, user?.id]);

  const selectedGenresSet = useMemo(
    () => new Set(selectedGenres.map((genre) => genre.toLowerCase())),
    [selectedGenres]
  );

  const filteredMusicians = useMemo(() => {
    if (selectedGenresSet.size === 0) return baseFilteredMusicians;
    return baseFilteredMusicians.filter((musician) =>
      (musician.genres ?? []).some((genre) => selectedGenresSet.has(genre.toLowerCase()))
    );
  }, [baseFilteredMusicians, selectedGenresSet]);

  const genreEntries = useMemo(() => {
    const counts = new Map<string, { genre: string; count: number }>();
    baseFilteredMusicians.forEach((musician) => {
      (musician.genres ?? []).forEach((genre) => {
        const key = normalizeGenreKey(genre);
        const current = counts.get(key);
        counts.set(key, {
          genre: current?.genre ?? formatGenreLabel(genre),
          count: (current?.count ?? 0) + 1,
        });
      });
    });

    const entries = Array.from(counts.values());
    selectedGenres.forEach((genre) => {
      const key = normalizeGenreKey(genre);
      if (!counts.has(key)) {
        entries.push({ genre: formatGenreLabel(genre), count: 0 });
      }
    });

    entries.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.genre.localeCompare(b.genre);
    });

    return entries;
  }, [baseFilteredMusicians, selectedGenres]);

  const toggleGenre = (genre: string) => {
    const normalizedGenre = formatGenreLabel(genre);

    setSelectedGenres((prev) => {
      const alreadySelected = prev.some((item) => normalizeGenreKey(item) === normalizeGenreKey(normalizedGenre));
      if (alreadySelected) {
        return prev.filter((item) => normalizeGenreKey(item) !== normalizeGenreKey(normalizedGenre));
      }

      return [...prev, normalizedGenre];
    });
  };

  const getGenreFontSize = (count: number): string => {
    if (count >= 6) return '1.125rem';
    if (count >= 4) return '1rem';
    if (count >= 2) return '0.9375rem';
    return '0.8125rem';
  };

  const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 16rem)'
  };

  const center = referenceLocation || defaultCenter;

  const renderMap = () => {
    // If no API key is provided, show fallback
    if (!googleMapsApiKey) {
      return (
        <div className="bg-gray-800 rounded-lg p-8 text-center\" style={{ height: 'calc(100vh - 16rem)' }}>
          <MapIcon className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Map View Unavailable</h3>
          <p className="text-gray-400 mb-4">
            Google Maps API key is required to display the map view.
          </p>
          <p className="text-sm text-gray-500">
            Please configure VITE_GOOGLE_MAPS_API_KEY in your environment variables.
          </p>
          <button
            onClick={() => setViewMode('list')}
            className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors"
          >
            Switch to List View
          </button>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="bg-red-500 text-white p-4 rounded-lg">
          Error loading maps. Please check your API key and try again.
        </div>
      );
    }

    if (!isLoaded) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-16rem)] bg-gray-800 rounded-lg">
          <div className="text-white">Loading maps...</div>
        </div>
      );
    }

    return (
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={13}
        options={{
          styles: [
            {
              featureType: 'all',
              elementType: 'all',
              stylers: [{ saturation: -100 }, { lightness: -20 }]
            },
            {
              featureType: 'water',
              elementType: 'all',
              stylers: [{ color: '#2d333c' }]
            }
          ]
        }}
      >
        {filteredMusicians.map((musician) => {
          if (!musician.location) return null;
          return (
            <Marker
              key={musician.id}
              position={{ lat: musician.location.lat, lng: musician.location.lng }}
              icon={{
                url: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='%239333ea' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z'/><circle cx='12' cy='10' r='3'/></svg>`,
                scaledSize: isLoaded ? new window.google.maps.Size(40, 40) : undefined,
                anchor: isLoaded ? new window.google.maps.Point(20, 40) : undefined,
              }}
              onClick={() => setSelectedMusician(musician.id)}
            />
          );
        })}
        
        {selectedMusician && (() => {
          const musician = filteredMusicians.find(m => m.id === selectedMusician);
          return musician ? (
            <InfoWindow
              position={{ lat: musician.location?.lat ?? center.lat, lng: musician.location?.lng ?? center.lng }}
              onCloseClick={() => setSelectedMusician(null)}
            >
              <div className="bg-gray-800 p-3 rounded-lg text-white" style={{ minWidth: '200px' }}>
                <h3 className="font-bold text-lg">{musician.name}</h3>
                <div className="flex items-center text-sm my-1">
                  <Music className="w-4 h-4 mr-1 text-purple-400" />
                  <span>{musician.instrument} - {musician.skillLevel}</span>
                </div>
                {musician.age !== null && (
                  <div className="flex items-center text-sm mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-600 text-white text-xs font-semibold">
                      Age {musician.age}
                    </span>
                  </div>
                )}
                <div className="flex items-center text-sm mb-2">
                  <MapPin className="w-4 h-4 mr-1 text-purple-400" />
                  <span>{musician.address || 'Location not provided'}</span>
                </div>
                <div className="flex gap-2 mt-2">
                  <a 
                    href={`/profile/${musician.id}`}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-center py-1 px-2 rounded text-sm"
                  >
                    View Profile
                  </a>
                </div>
              </div>
            </InfoWindow>
          ) : null;
        })()}
      </GoogleMap>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Find Musicians Near You</h1>
          <p className="text-gray-400">Connect with local musicians who match your criteria</p>
        </div>
        
        <div className="flex space-x-2 mt-4 md:mt-0">
          <button 
            className={`px-4 py-2 rounded-md flex items-center ${
              viewMode === 'list' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            } transition-colors`}
            onClick={() => setViewMode('list')}
          >
            <List className="w-5 h-5 mr-2" />
            List
          </button>
          
          {/* <button 
            className={`px-4 py-2 rounded-md flex items-center ${
              viewMode === 'map' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            } transition-colors`}
            onClick={() => setViewMode('map')}
          >
            <MapIcon className="w-5 h-5 mr-2" />
            Map
          </button> */}
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg p-3 md:p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          <div className="md:col-span-8">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search by location
            </label>
            <LocationSelector onLocationSelect={handleLocationSelect} />
            <p className="text-xs text-gray-400 mt-2">
              {searchLocation
                ? '(Server search uses the selected location; the distance filter is also applied client-side)'
                : '(If no location is selected, distance is calculated client-side using your profile position, if available)'}
            </p>
            {searchAddress && (
              <p className="text-xs text-gray-400 mt-2">
                Selected location: {searchAddress}
              </p>
            )}
            <div className="mt-3">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm text-gray-300">Maximum Distance: {maxDistance} km</label>
                {googleMapsApiKey && (
                  <span className="text-xs text-green-400">🗺️ Map view enabled</span>
                )}
              </div>
              <input
                type="range"
                min="1"
                max="100"
                value={maxDistance}
                onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
            </div>
          </div>

          <div className="md:col-span-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Instrument</label>
              <select
                value={selectedInstrument}
                onChange={(e) => setSelectedInstrument(e.target.value)}
                className="w-full pl-3 pr-10 py-1.5 text-sm bg-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  paddingRight: '2.5rem'
                }}
              >
                {instruments.map((instrument) => (
                  <option key={instrument} value={instrument}>
                    {instrument}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Level</label>
              <select
                value={selectedSkillLevel}
                onChange={(e) => setSelectedSkillLevel(e.target.value)}
                className="w-full pl-3 pr-10 py-1.5 text-sm bg-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  paddingRight: '2.5rem'
                }}
              >
                {skillLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-400">Genres</label>
                {selectedGenres.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedGenres([])}
                    className="text-xs text-purple-300 hover:text-purple-200"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              {genreEntries.length === 0 ? (
                <p className="text-xs text-gray-500">No genres available for current filters.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {genreEntries.map(({ genre, count }) => {
                    const isSelected = selectedGenres.includes(genre);
                    return (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => toggleGenre(genre)}
                        aria-pressed={isSelected}
                        className={`px-3 py-1 rounded-full border transition-colors ${
                          isSelected
                            ? 'bg-purple-600 border-purple-500 text-white'
                            : 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                        }`}
                        style={{ fontSize: getGenreFontSize(Math.max(1, count)) }}
                        title={count > 0 ? `${count} profiles` : 'No profiles for this genre'}
                      >
                        {genre}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Results Count */}
      <div className="mb-6">
        {searchError && (
          <div className="mb-3">
            <AlertMessage variant="error">{searchError}</AlertMessage>
          </div>
        )}
        <p className="text-gray-300">
          <span className="font-bold text-white">{filteredMusicians.length}</span> musicians found matching your criteria
        </p>
        {isLoading && <p className="text-sm text-gray-400 mt-2">Loading results...</p>}
        {!hasMoreResults && filteredMusicians.length > 0 && (
          <p className="text-sm text-gray-500 mt-2">No more results.</p>
        )}
      </div>
      
      {/* Results List View */}
      {viewMode === 'list' && (
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {filteredMusicians.map((musician) => (
            <MusicianCard
              key={musician.id}
              id={musician.id}
              name={musician.name}
              instrument={musician.instrument}
              skillLevel={musician.skillLevel}
              location={musician.address || 'Location not provided'}
              distance={musician.distance}
              age={musician.age}
            />
          ))}
        </motion.div>
      )}
      
      {/* Map View */}
      {viewMode === 'map' && (
        <motion.div
          className="rounded-lg overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {renderMap()}
        </motion.div>
      )}
    </div>
  );
};

export default SearchPage;
